import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowLeftRight, ChevronDown, ClipboardCheck, Download, Package, Plus, Search, Tags, Trash2, Upload, Warehouse, X } from "lucide-react";
import { api, API, money } from "@/lib/api";
import { Pagination, usePagination } from "@/components/Pagination";
import { SearchSelect } from "@/components/SearchSelect";

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? d.split("-").reverse().join("-") : "—");
const FIELDS = [
  { key: "name", label: "Nama Produk", required: true },
  { key: "code", label: "Kode Produk" },
  { key: "sku", label: "SKU", required: true },
  { key: "category", label: "Kategori" },
  { key: "unit", label: "Unit" },
  { key: "purchase_price", label: "Harga Beli", type: "number" },
  { key: "selling_price", label: "Harga Jual", type: "number" },
  { key: "stock", label: "Stok", type: "number" },
  { key: "min_stock", label: "Batas Minimum", type: "number" },
];

export default function Products() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [tab, setTab] = useState("produk");
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selectedAdj, setSelectedAdj] = useState(null);
  const [message, setMessage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [whFilter, setWhFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [whStock, setWhStock] = useState({});
  const [detail, setDetail] = useState(null);
  const load = () => {
    api("/products").then(setRows).catch((e) => setMessage({ text: e.message, type: "error" }));
    api("/inventory/summary").then(setSummary).catch(() => {});
    api("/warehouses").then(setWarehouses).catch(() => {});
    api("/stock-adjustments").then(setAdjustments).catch(() => {});
    api("/product-categories").then(setCategories).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!whFilter) { setWhStock({}); return; }
    api(`/inventory/warehouse-stock/${whFilter}`).then((r) => setWhStock(r.stock || {})).catch(() => setWhStock({}));
  }, [whFilter]);
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuOpen]);
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 7000);
    return () => clearTimeout(t);
  }, [message]);
  const filtered = rows
    .filter((r) => [r.name, r.code, r.sku, r.category].join(" ").toLowerCase().includes(search.toLowerCase()))
    .filter((r) => !catFilter || (r.category || "") === catFilter);
  const stockOf = (p) => (whFilter ? (whStock[p.id] ?? 0) : (p.stock ?? 0));
  const whName = warehouses.find((w) => w.id === whFilter)?.name || "";
  const filteredAdj = adjustments.filter((r) => [r.number, r.warehouse_name, r.date].join(" ").toLowerCase().includes(search.toLowerCase()));
  const pager = usePagination(filtered, 25, search);
  const adjPager = usePagination(filteredAdj, 25, search);
  const done = (text) => { setModal(null); setEditing(null); setMessage({ text, type: "success" }); load(); };
  const stockBadge = (p) => {
    const stock = p.stock || 0, minimum = p.min_stock || 0;
    if (stock <= 0) return <span className="stock-chip out" data-testid={`product-stock-${p.id}`}><AlertTriangle size={11} /> Habis</span>;
    if (minimum > 0 && stock <= minimum) return <span className="stock-chip low" data-testid={`product-stock-${p.id}`}><AlertTriangle size={11} /> Menipis</span>;
    return null;
  };
  const remove = async () => {
    try {
      await api(`/products/${deleting.id}`, { method: "DELETE" });
      setMessage({ text: `Produk "${deleting.name}" berhasil dihapus`, type: "success" });
      load();
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setDeleting(null);
  };
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div><p className="eyebrow">PRODUK</p><h1>Produk</h1></div>
        <div className="cb-menu-cell prod-action-wrap">
          <button data-testid="product-action-button" className="primary-btn" onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}>Tindakan <ChevronDown size={14} /></button>
          {menuOpen && (
            <div className="cb-menu prod-menu" data-testid="product-action-menu" onClick={(e) => e.stopPropagation()}>
              <small>PRODUK</small>
              <button data-testid="action-add-product" onClick={() => { setMenuOpen(false); setEditing({}); setModal("form"); }}><Plus size={13} /> Tambah produk baru</button>
              <button data-testid="action-import" onClick={() => { setMenuOpen(false); setModal("import"); }}><Upload size={13} /> Impor / update massal (Excel)</button>
              <button data-testid="action-export" onClick={() => { setMenuOpen(false); setModal("export"); }}><Download size={13} /> Ekspor ke Excel</button>
              <button data-testid="action-categories" onClick={() => { setMenuOpen(false); setModal("categories"); }}><Tags size={13} /> Kelola kategori</button>
              <small>GUDANG</small>
              <button data-testid="action-add-warehouse" onClick={() => { setMenuOpen(false); setModal("warehouse"); }}><Warehouse size={13} /> Tambah gudang baru</button>
              <button data-testid="action-opname" onClick={() => { setMenuOpen(false); setModal("opname"); }}><ClipboardCheck size={13} /> Sesuaikan stok (stock opname)</button>
              <button data-testid="action-wh-transfer" onClick={() => { setMenuOpen(false); setModal("transfer"); }}><ArrowLeftRight size={13} /> Transfer gudang</button>
            </div>
          )}
        </div>
      </div>
      {message && <div data-testid={message.type === "error" ? "product-error-message" : "product-success-message"} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button data-testid="product-dismiss-message" onClick={() => setMessage(null)}><X size={14} /></button></div>}
      {summary && (
        <div className="prod-cards" data-testid="product-summary-cards">
          <div className="prod-card green" data-testid="stock-available-card"><div className="prod-card-head"><span>Stok tersedia</span><Package size={15} /></div><small>Total produk</small><strong>{summary.available}</strong></div>
          <div className="prod-card yellow" data-testid="stock-low-card"><div className="prod-card-head"><span>Stok segera habis</span><AlertTriangle size={15} /></div><small>Total produk</small><strong>{summary.low}</strong></div>
          <div className="prod-card red" data-testid="stock-out-card"><div className="prod-card-head"><span>Stok habis</span><AlertTriangle size={15} /></div><small>Total produk</small><strong>{summary.out}</strong></div>
          <div className="prod-card blue" data-testid="warehouse-card"><div className="prod-card-head"><span>Gudang</span><Warehouse size={15} /></div><small>Terdaftar</small><strong>{summary.warehouses}</strong></div>
        </div>
      )}
      <div className="report-tabs" data-testid="product-tabs">
        <button data-testid="tab-products" className={tab === "produk" ? "active" : ""} onClick={() => setTab("produk")}>Daftar produk</button>
        <button data-testid="tab-adjustments" className={tab === "penyesuaian" ? "active" : ""} onClick={() => setTab("penyesuaian")}>Daftar penyesuaian stok <i className="cb-badge blue inline">{adjustments.length}</i></button>
      </div>
      <div className="workflow-toolbar surface prod-toolbar">
        <button data-testid="toolbar-import" className="secondary-btn small" onClick={() => setModal("import")}><Upload size={14} /> Impor</button>
        <button data-testid="toolbar-export" className="secondary-btn small" onClick={() => setModal("export")}><Download size={14} /> Ekspor</button>
        {tab === "produk" && (
          <div className="prod-filters" data-testid="product-filters">
            <div className="prod-filter-ss" data-testid="product-filter-warehouse-wrap">
              <SearchSelect testid="product-filter-warehouse" value={whFilter} onChange={setWhFilter} placeholder="Semua gudang"
                options={[{ value: "", label: "Semua gudang" }, ...warehouses.map((w) => ({ value: w.id, label: w.name }))]} />
            </div>
            <div className="prod-filter-ss" data-testid="product-filter-category-wrap">
              <SearchSelect testid="product-filter-category" value={catFilter} onChange={setCatFilter} placeholder="Semua kategori"
                options={[{ value: "", label: "Semua kategori" }, ...categories.map((c) => ({ value: c.name, label: c.name })), ...(catFilter && !categories.some((c) => c.name === catFilter) ? [{ value: catFilter, label: catFilter }] : [])]} />
            </div>
            {(whFilter || catFilter) && <button data-testid="product-filter-reset" className="table-action" onClick={() => { setWhFilter(""); setCatFilter(""); }}>Reset</button>}
          </div>
        )}
        <div className="workflow-search prod-search"><Search size={16} /><input data-testid="product-search-input" placeholder="Cari produk..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <span className="record-count">{tab === "produk" ? filtered.length : filteredAdj.length} data</span>
      </div>
      {tab === "produk" && (
        <div className="surface workflow-table-wrap">
          <table className="workflow-table" data-testid="product-table">
            <thead><tr><th>NAMA PRODUK</th><th>KODE PRODUK</th><th>SKU</th><th>KATEGORI</th><th className="right">{whFilter ? `STOK · ${whName.toUpperCase()}` : "TOTAL STOK"}</th><th className="right">BATAS MINIMUM</th><th>UNIT</th><th className="right">HARGA BELI</th><th className="right">HARGA JUAL</th><th>AKSI</th></tr></thead>
            <tbody>
              {pager.pageItems.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="prod-row" onClick={() => setDetail(p)}>
                  <td className="primary-cell"><span className="cb-account-link">{p.name}</span> {stockBadge(p)}</td>
                  <td>{p.code || "—"}</td>
                  <td>{p.sku}</td>
                  <td>{p.category ? <span className="cat-chip">{p.category}</span> : "—"}</td>
                  <td className={`right mono ${stockOf(p) <= 0 ? "negative-amount" : ""}`}>{p.type === "Service" ? "—" : stockOf(p)}</td>
                  <td className="right mono">{p.min_stock ?? 0}</td>
                  <td>{p.unit || "Pcs"}</td>
                  <td className="right mono">{money(p.purchase_price)}</td>
                  <td className="right mono">{money(p.selling_price)}</td>
                  <td>
                    <span className="row-actions">
                      <button data-testid={`product-edit-${p.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setEditing(p); setModal("form"); }}>Edit</button>
                      <button data-testid={`product-delete-${p.id}`} className="table-action danger" onClick={(e) => { e.stopPropagation(); setDeleting(p); }}>Hapus</button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="empty-state" data-testid="product-empty-state"><Package size={25} /><strong>Belum ada produk</strong><span>Tambah produk baru atau impor dari Excel.</span></div>}
          <Pagination pager={pager} testid="product-pagination" />
        </div>
      )}
      {tab === "penyesuaian" && (
        <div className="surface workflow-table-wrap">
          <table className="workflow-table" data-testid="adjustment-table">
            <thead><tr><th>NOMOR</th><th>TANGGAL</th><th>GUDANG</th><th className="right">JUMLAH PRODUK</th><th className="right">NILAI PENYESUAIAN</th><th>JURNAL</th><th>AKSI</th></tr></thead>
            <tbody>
              {adjPager.pageItems.map((a) => (
                <tr key={a.id} data-testid={`adjustment-row-${a.id}`} onClick={() => setSelectedAdj(a)}>
                  <td className="primary-cell">{a.number}</td>
                  <td>{fmtDate(a.date)}</td>
                  <td>{a.warehouse_name}</td>
                  <td className="right mono">{(a.lines || []).length}</td>
                  <td className={`right mono ${a.total_value < 0 ? "negative-amount" : ""}`}>{money(a.total_value)}</td>
                  <td>{a.journal_number || "—"}</td>
                  <td><button data-testid={`adjustment-open-${a.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setSelectedAdj(a); }}>Detail</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredAdj.length === 0 && <div className="empty-state" data-testid="adjustment-empty-state"><ClipboardCheck size={25} /><strong>Belum ada penyesuaian stok</strong><span>Lakukan stock opname pertama Anda melalui menu Tindakan.</span></div>}
          <Pagination pager={adjPager} testid="adjustment-pagination" />
        </div>
      )}
      {modal === "form" && <ProductFormModal initial={editing?.id ? editing : null} onClose={() => { setModal(null); setEditing(null); }} onDone={(r, isEdit) => done(`Produk "${r.name}" berhasil ${isEdit ? "diubah" : "ditambahkan"}`)} />}
      {modal === "import" && <ImportModal warehouses={warehouses} onClose={() => setModal(null)} onDone={(res) => done(`Impor selesai: ${res.created} produk baru, ${res.updated} diperbarui${res.errors.length ? `, ${res.errors.length} error` : ""}`)} />}
      {modal === "export" && <ExportModal warehouses={warehouses} onClose={() => setModal(null)} onDone={(whName2) => done(whName2 ? `Produk diekspor — kolom stok gudang ${whName2}` : "Produk diekspor — stok total semua gudang")} onError={(t) => { setModal(null); setMessage({ text: t, type: "error" }); }} />}
      {modal === "warehouse" && <WarehouseModal onClose={() => setModal(null)} onDone={(r) => done(`Gudang "${r.name}" berhasil ditambahkan`)} />}
      {modal === "opname" && <OpnameModal warehouses={warehouses} products={rows} onClose={() => setModal(null)} onDone={(r) => done(`${r.number} tersimpan — stok diperbarui${r.journal_number ? ` dan jurnal ${r.journal_number} diposting` : ""}`)} />}
      {modal === "transfer" && <WhTransferModal warehouses={warehouses} products={rows} onClose={() => setModal(null)} onDone={(r) => done(`${r.number} — transfer dari ${r.from_warehouse} ke ${r.to_warehouse} berhasil`)} />}
      {modal === "categories" && <CategoryModal onClose={() => { setModal(null); load(); }} />}
      {detail && <ProductDetailDrawer product={detail} onClose={() => setDetail(null)} onEdit={() => { setEditing(detail); setDetail(null); setModal("form"); }} />}
      {deleting && (
        <div className="modal-overlay" data-testid="product-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
            <div className="form-panel">
              <div className="section-head"><div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Produk?</h2></div><button className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button></div>
              <p className="muted delete-warning">"{deleting.name}" akan dihapus permanen. Produk yang sudah dipakai transaksi tidak dapat dihapus.</p>
              <div className="form-actions">
                <button data-testid="product-delete-confirm" className="danger-btn" onClick={remove}>Ya, Hapus</button>
                <button data-testid="product-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedAdj && (
        <>
          <div className="drawer-backdrop" onClick={() => setSelectedAdj(null)} />
          <div className="detail-drawer" data-testid="adjustment-detail-drawer">
            <div className="drawer-head"><div><p className="eyebrow">PENYESUAIAN STOK</p><h2>{selectedAdj.number}</h2></div><button data-testid="adjustment-close-detail" className="icon-btn" onClick={() => setSelectedAdj(null)}><X size={18} /></button></div>
            <div className="detail-fields">
              <div><small>gudang</small><strong>{selectedAdj.warehouse_name}</strong></div>
              <div><small>tanggal</small><strong>{fmtDate(selectedAdj.date)}</strong></div>
              <div><small>jurnal</small><strong>{selectedAdj.journal_number || "—"}</strong></div>
              <div><small>nilai penyesuaian</small><strong>{money(selectedAdj.total_value)}</strong></div>
              {selectedAdj.memo && <div><small>memo</small><strong>{selectedAdj.memo}</strong></div>}
            </div>
            <div className="doc-lines">
              <h3>Rincian penyesuaian</h3>
              <table className="doc-line-table">
                <thead><tr><th>PRODUK</th><th className="right">TERCATAT</th><th className="right">FISIK</th><th className="right">SELISIH</th><th className="right">NILAI</th></tr></thead>
                <tbody>
                  {(selectedAdj.lines || []).map((l, i) => (
                    <tr key={i}><td>{l.name}<br /><small className="muted">{l.sku}</small></td><td className="right mono">{l.before}</td><td className="right mono">{l.counted}</td><td className={`right mono ${l.diff < 0 ? "negative-amount" : ""}`}>{l.diff > 0 ? `+${l.diff}` : l.diff}</td><td className="right mono">{money(l.value)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="drawer-actions">
              <button data-testid="adjustment-delete-button" className="danger-btn" onClick={async () => {
                try {
                  await api(`/stock-adjustments/${selectedAdj.id}`, { method: "DELETE" });
                  setSelectedAdj(null);
                  setMessage({ text: `${selectedAdj.number} dihapus — stok dikembalikan dan jurnal dibatalkan (void)`, type: "success" });
                  load();
                } catch (e) { setMessage({ text: e.message, type: "error" }); }
              }}>Hapus &amp; Kembalikan Stok</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const UNITS = ["Pcs", "Unit", "Box", "Pack", "Lusin", "Kg", "Gram", "Liter", "Meter", "Roll", "Set"];
const TAX_OPTIONS = ["", "PPN 11%", "PPN 12%"];

export function ProductFormModal({ initial, defaultName, onClose, onDone }) {
  const [form, setForm] = useState(initial || {
    name: defaultName || "", unit: "Pcs", code: "", sku: "", category: "", type: "Inventory",
    buy: true, sell: true, purchase_price: "", selling_price: "", stock: "", min_stock: "", warehouse_id: "",
    purchase_account: "5-50000", sales_account: "4-40000", purchase_tax: "", sales_tax: "",
  });
  const [accounts, setAccounts] = useState([]);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api("/accounts").then(setAccounts).catch(() => {}); }, []);
  useEffect(() => { api("/product-categories").then(setCats).catch(() => {}); }, []);
  const [whs, setWhs] = useState([]);
  const [unitOpts, setUnitOpts] = useState(UNITS);
  const [newUnit, setNewUnit] = useState(null);
  useEffect(() => {
    api("/products").then((ps) => {
      const extra = [...new Set(ps.map((p) => (p.unit || "").trim()).filter((u) => u && !UNITS.includes(u)))].sort();
      if (extra.length) setUnitOpts([...UNITS, ...extra]);
    }).catch(() => {});
  }, []);
  const saveNewUnit = () => {
    const u = (newUnit || "").trim();
    if (u) {
      setForm((f) => ({ ...f, unit: u }));
      setUnitOpts((x) => (x.some((v) => v.toLowerCase() === u.toLowerCase()) ? x : [...x, u]));
    }
    setNewUnit(null);
  };
  useEffect(() => {
    if (initial) return;
    api("/warehouses").then((w) => {
      setWhs(w);
      const main = w.find((x) => x.is_main) || w[0];
      if (main) setForm((f) => ({ ...f, warehouse_id: f.warehouse_id || main.id }));
    }).catch(() => {});
  }, []); // eslint-disable-line
  const createCategory = async (nm) => {
    nm = (nm || "").trim();
    if (!nm) return;
    try {
      const c = await api("/product-categories", { method: "POST", body: JSON.stringify({ name: nm }) });
      setCats((x) => [...x, c].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e4) {
      if (!/sudah ada/i.test(e4.message || "")) { setError(e4.message); return; }
    }
    setForm((f) => ({ ...f, category: nm }));
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const track = form.type !== "Service";
  const buyAccounts = accounts.filter((a) => ["Cost of Sales", "Expenses", "Other Expenses"].includes(a.category));
  const sellAccounts = accounts.filter((a) => ["Revenue", "Other Income"].includes(a.category));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = { ...form };
      if (initial) delete payload.warehouse_id;
      if (!payload.sku?.trim()) payload.sku = payload.code?.trim() || `${(payload.name || "PRD").replaceAll(" ", "-").toUpperCase().slice(0, 12)}-${Date.now().toString().slice(-5)}`;
      ["purchase_price", "selling_price", "stock", "min_stock"].forEach((k) => { payload[k] = Number(payload[k]) || 0; });
      payload.buy = Boolean(payload.buy); payload.sell = Boolean(payload.sell);
      const r = initial
        ? await api(`/products/${initial.id}`, { method: "PATCH", body: JSON.stringify(payload) })
        : await api("/products", { method: "POST", body: JSON.stringify(payload) });
      onDone(r, Boolean(initial));
    } catch (e2) { setError(e2.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="product-form-modal" onClick={onClose}>
      <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">PRODUK</p><h2>{initial ? "Edit Produk" : "Tambah Produk Baru"}</h2></div><button data-testid="product-form-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          {error && <div className="journal-balance bad" data-testid="product-form-error">{error}</div>}
          <form onSubmit={submit}>
            <div className="form-grid">
              <label>Nama produk *<input data-testid="product-field-name" value={form.name ?? ""} required onChange={(e) => set("name", e.target.value)} placeholder="Masukkan nama produk" /></label>
              <label>Unit *
                {newUnit === null ? (
                  <select data-testid="product-field-unit" value={form.unit ?? "Pcs"} onChange={(e) => { if (e.target.value === "__new") setNewUnit(""); else set("unit", e.target.value); }}>
                    {unitOpts.map((u) => <option key={u} value={u}>{u}</option>)}
                    {form.unit && !unitOpts.includes(form.unit) && <option value={form.unit}>{form.unit}</option>}
                    <option value="__new">+ Tambah unit baru...</option>
                  </select>
                ) : (
                  <span className="cat-quick-add">
                    <input data-testid="product-new-unit-input" autoFocus value={newUnit} placeholder="mis. Yard, Kodi, Rim" onChange={(e) => setNewUnit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveNewUnit(); } }} />
                    <button type="button" data-testid="product-new-unit-save" className="table-action" onClick={saveNewUnit}>Simpan</button>
                    <button type="button" data-testid="product-new-unit-cancel" className="table-action" onClick={() => setNewUnit(null)}>Batal</button>
                  </span>
                )}
              </label>
              <label>Kode / SKU produk<input data-testid="product-field-sku" value={form.sku ?? ""} onChange={(e) => setForm({ ...form, sku: e.target.value, code: e.target.value })} placeholder="Otomatis jika kosong" /></label>
              <label>Kategori produk
                <SearchSelect testid="product-field-category" value={form.category ?? ""} onChange={(v) => set("category", v)} placeholder="Tanpa kategori"
                  options={[{ value: "", label: "Tanpa kategori" }, ...cats.map((c) => ({ value: c.name, label: c.name })), ...(form.category && !cats.some((c) => c.name === form.category) ? [{ value: form.category, label: form.category }] : [])]}
                  onCreate={createCategory} createLabel="Tambah kategori baru" createTestid="product-field-category-add-new" />
              </label>
              <label>Tipe produk *<input value="Single" disabled /></label>
              <label>Pelacakan persediaan *
                <div className="prod-track" data-testid="product-field-track">
                  <label className="prod-radio"><input type="radio" name="track" checked={track} onChange={() => set("type", "Inventory")} /> Lacak</label>
                  <label className="prod-radio"><input type="radio" name="track" checked={!track} onChange={() => set("type", "Service")} /> Tidak dilacak</label>
                </div>
              </label>
            </div>
            <div className="drawer-section-title">Harga &amp; Persediaan</div>
            <label className="asset-check">
              <input data-testid="product-field-buy" type="checkbox" checked={Boolean(form.buy ?? true)} onChange={(e) => set("buy", e.target.checked)} />
              Saya beli produk ini
            </label>
            {(form.buy ?? true) && (
              <div className="form-grid prod-sub-grid">
                <label>Harga satuan (beli)<input data-testid="product-field-purchase_price" type="number" min="0" placeholder="0" value={form.purchase_price ?? ""} onChange={(e) => set("purchase_price", e.target.value)} /></label>
                <label>Akun pembelian *
                  <SearchSelect testid="product-field-purchase-account" value={form.purchase_account ?? "5-50000"} onChange={(v) => set("purchase_account", v)} placeholder="Pilih akun..."
                    options={(buyAccounts.length ? buyAccounts : [{ code: "5-50000", name: "Beban Pokok Pendapatan" }]).map((a) => ({ value: a.code, label: `(${a.code}) ${a.name}` }))} />
                </label>
                <label>Pajak pembelian
                  <select data-testid="product-field-purchase-tax" value={form.purchase_tax ?? ""} onChange={(e) => set("purchase_tax", e.target.value)}>
                    {TAX_OPTIONS.map((t) => <option key={t} value={t}>{t || "Tanpa pajak"}</option>)}
                  </select>
                </label>
              </div>
            )}
            <label className="asset-check">
              <input data-testid="product-field-sell" type="checkbox" checked={Boolean(form.sell ?? true)} onChange={(e) => set("sell", e.target.checked)} />
              Saya jual produk ini
            </label>
            {(form.sell ?? true) && (
              <div className="form-grid prod-sub-grid">
                <label>Harga satuan (jual)<input data-testid="product-field-selling_price" type="number" min="0" placeholder="0" value={form.selling_price ?? ""} onChange={(e) => set("selling_price", e.target.value)} /></label>
                <label>Akun penjualan *
                  <SearchSelect testid="product-field-sales-account" value={form.sales_account ?? "4-40000"} onChange={(v) => set("sales_account", v)} placeholder="Pilih akun..."
                    options={(sellAccounts.length ? sellAccounts : [{ code: "4-40000", name: "Pendapatan Penjualan" }]).map((a) => ({ value: a.code, label: `(${a.code}) ${a.name}` }))} />
                </label>
                <label>Pajak penjualan
                  <select data-testid="product-field-sales-tax" value={form.sales_tax ?? ""} onChange={(e) => set("sales_tax", e.target.value)}>
                    {TAX_OPTIONS.map((t) => <option key={t} value={t}>{t || "Tanpa pajak"}</option>)}
                  </select>
                </label>
              </div>
            )}
            {track && (
              <div className="form-grid prod-sub-grid">
                <label>Stok awal<input data-testid="product-field-stock" type="number" min="0" placeholder="0" value={form.stock ?? ""} onChange={(e) => set("stock", e.target.value)} /></label>
                <label>Batas minimum stok<input data-testid="product-field-min_stock" type="number" min="0" placeholder="0" value={form.min_stock ?? ""} onChange={(e) => set("min_stock", e.target.value)} /></label>
                {!initial && whs.length > 0 && (
                  <label>Gudang penyimpanan *
                    <SearchSelect testid="product-field-warehouse" value={form.warehouse_id ?? ""} onChange={(v) => set("warehouse_id", v)} placeholder="Pilih gudang..."
                      options={whs.map((w) => ({ value: w.id, label: `${w.name}${w.is_main ? " (Utama)" : ""}` }))} />
                  </label>
                )}
              </div>
            )}
            <div className="form-actions">
              <button data-testid="product-form-save" className="primary-btn" type="submit" disabled={saving}>{saving ? "Menyimpan..." : "Simpan"}</button>
              <button data-testid="product-form-cancel" className="secondary-btn" type="button" onClick={onClose}>Tutup</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ProductDetailDrawer({ product, onClose, onEdit }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api(`/products/${product.id}/warehouse-stock`).then(setData).catch((e) => setError(e.message)); }, [product.id]);
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="detail-drawer" data-testid="product-detail-drawer">
        <div className="drawer-head"><div><p className="eyebrow">DETAIL PRODUK</p><h2>{product.name}</h2></div><button data-testid="product-detail-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
        <div className="detail-fields">
          <div><small>sku</small><strong>{product.sku || "—"}</strong></div>
          <div><small>kategori</small><strong>{product.category || "—"}</strong></div>
          <div><small>unit</small><strong>{product.unit || "Pcs"}</strong></div>
          <div><small>harga beli</small><strong>{money(product.purchase_price)}</strong></div>
          <div><small>harga jual</small><strong>{money(product.selling_price)}</strong></div>
          <div><small>total stok</small><strong data-testid="product-detail-total-stock">{product.type === "Service" ? "—" : (product.stock ?? 0)}</strong></div>
        </div>
        <div className="doc-lines">
          <h3>Stok per gudang</h3>
          {error && <div className="journal-balance bad">{error}</div>}
          {product.type === "Service" ? (
            <p className="muted">Produk jasa — stok tidak dilacak.</p>
          ) : (
            <table className="doc-line-table" data-testid="product-warehouse-table">
              <thead><tr><th>GUDANG</th><th>LOKASI</th><th className="right">STOK</th></tr></thead>
              <tbody>
                {(data?.rows || []).map((r) => (
                  <tr key={r.warehouse_id} data-testid={`product-wh-row-${r.warehouse_id}`}>
                    <td>{r.warehouse_name}</td><td>{r.location || "—"}</td>
                    <td className={`right mono ${r.qty < 0 ? "negative-amount" : ""}`}>{r.qty}</td>
                  </tr>
                ))}
                {data && data.rows.length === 0 && <tr><td colSpan={3} className="muted">Belum ada gudang terdaftar.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        <div className="drawer-actions">
          <button data-testid="product-detail-edit" className="secondary-btn" onClick={onEdit}>Edit Produk</button>
        </div>
      </div>
    </>
  );
}

function CategoryModal({ onClose }) {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [error, setError] = useState("");
  const load = () => api("/product-categories").then(setCats).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);
  const run = async (fn) => { setError(""); try { await fn(); load(); } catch (e) { setError(e.message); } };
  const add = () => run(async () => { await api("/product-categories", { method: "POST", body: JSON.stringify({ name }) }); setName(""); });
  const rename = (id) => run(async () => { await api(`/product-categories/${id}`, { method: "PATCH", body: JSON.stringify({ name: editName }) }); setEditingId(null); });
  const remove = (c) => run(() => api(`/product-categories/${c.id}`, { method: "DELETE" }));
  return (
    <div className="modal-overlay" data-testid="category-modal" onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">KATEGORI PRODUK</p><h2>Kelola Kategori</h2></div><button data-testid="category-modal-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          <p className="muted">Pisahkan produk berdasarkan variasi, mis. <b>Bahan Baku</b>, <b>Produk Jadi - Atasan</b>, <b>Bawahan</b>, dll.</p>
          {error && <div className="journal-balance bad" data-testid="category-error">{error}</div>}
          <div className="cat-quick-add">
            <input data-testid="category-new-input" value={name} placeholder="Nama kategori baru" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && add()} />
            <button data-testid="category-new-save" className="primary-btn small-btn" disabled={!name.trim()} onClick={add}><Plus size={13} /> Tambah</button>
          </div>
          <table className="doc-line-table cat-table" data-testid="category-table">
            <thead><tr><th>KATEGORI</th><th className="right">PRODUK</th><th className="right">AKSI</th></tr></thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id} data-testid={`category-row-${c.id}`}>
                  <td>
                    {editingId === c.id
                      ? <input data-testid="category-edit-input" autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(c.id)} />
                      : <span className="cat-chip">{c.name}</span>}
                  </td>
                  <td className="right mono">{c.product_count}</td>
                  <td className="right">
                    {editingId === c.id ? (
                      <span className="row-actions">
                        <button data-testid={`category-save-${c.id}`} className="table-action" onClick={() => rename(c.id)}>Simpan</button>
                        <button className="table-action" onClick={() => setEditingId(null)}>Batal</button>
                      </span>
                    ) : (
                      <span className="row-actions">
                        <button data-testid={`category-rename-${c.id}`} className="table-action" onClick={() => { setEditingId(c.id); setEditName(c.name); }}>Ubah</button>
                        <button data-testid={`category-delete-${c.id}`} className="table-action danger" onClick={() => remove(c)}><Trash2 size={12} /></button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {cats.length === 0 && <tr><td colSpan={3} className="muted">Belum ada kategori — tambahkan di atas.</td></tr>}
            </tbody>
          </table>
          <p className="muted">Mengubah nama kategori otomatis memperbarui semua produk di dalamnya.</p>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ warehouses = [], onClose, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const main = warehouses.find((w) => w.is_main) || warehouses[0];
  const [whId, setWhId] = useState(main?.id || "");
  const upload = async () => {
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (whId) fd.append("warehouse_id", whId);
      const r = await fetch(`${API}/inventory/import`, { method: "POST", credentials: "include", body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Impor gagal");
      if (data.errors.length > 0) setResult(data);
      else onDone(data);
    } catch (e) { setError(e.message); }
    setUploading(false);
  };
  return (
    <div className="modal-overlay" data-testid="import-modal" onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">IMPOR / UPDATE MASSAL</p><h2>Impor Produk dari Excel</h2></div><button data-testid="import-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          <p className="muted delete-warning">Format kolom: Nama Produk, Kode Produk, SKU, Kategori, Unit, Harga Beli, Harga Jual, Stok, Batas Minimum. Baris dengan SKU yang sudah ada akan <b>diperbarui (update massal)</b>, SKU baru akan ditambahkan. Gunakan tombol Ekspor untuk mendapatkan template.</p>
          {error && <div className="journal-balance bad" data-testid="import-error">{error}</div>}
          {warehouses.length > 0 && (
            <label className="import-wh-label">Gudang tujuan stok
              <select data-testid="import-warehouse-select" value={whId} onChange={(e) => setWhId(e.target.value)}>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}{w.is_main ? " (Utama)" : ""}</option>)}
              </select>
            </label>
          )}
          <div className="exp-drop" data-testid="import-drop" onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0] || null); }}>
            {file ? <span className="exp-file-name">{file.name}</span> : <>Tarik file .xlsx ke sini, atau <b>pilih file</b></>}
          </div>
          <input ref={fileRef} data-testid="import-file-input" type="file" accept=".xlsx" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {result && (
            <div className="import-result" data-testid="import-result">
              <p><b>{result.created}</b> produk baru · <b>{result.updated}</b> diperbarui · <b>{result.errors.length}</b> error</p>
              <ul>{result.errors.map((er, i) => <li key={i}>{er}</li>)}</ul>
              <button className="secondary-btn small" data-testid="import-finish" onClick={() => onDone(result)}>Selesai</button>
            </div>
          )}
          {!result && (
            <div className="form-actions">
              <button data-testid="import-upload" className="primary-btn" disabled={!file || uploading} onClick={upload}>{uploading ? "Mengunggah..." : "Impor Sekarang"}</button>
              <button data-testid="import-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExportModal({ warehouses = [], onClose, onDone, onError }) {
  const main = warehouses.find((w) => w.is_main) || warehouses[0];
  const [whId, setWhId] = useState(main?.id || "");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const q = whId ? `?warehouse_id=${whId}` : "";
      const r = await fetch(`${API}/inventory/export${q}`, { credentials: "include" });
      if (!r.ok) throw new Error("Gagal mengekspor produk");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "produk.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
      onDone(whId ? (warehouses.find((w) => w.id === whId)?.name || "") : "");
    } catch (e) { onError(e.message); }
    setBusy(false);
  };
  return (
    <div className="modal-overlay" data-testid="export-modal" onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">EKSPOR PRODUK</p><h2>Ekspor ke Excel</h2></div><button data-testid="export-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          <p className="muted">Pilih gudang untuk kolom stok pada file Excel. Default: Gudang Utama.</p>
          <label className="import-wh-label">Gudang
            <select data-testid="export-warehouse-select" value={whId} onChange={(e) => setWhId(e.target.value)}>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}{w.is_main ? " (Utama)" : ""}</option>)}
              <option value="">Semua gudang (stok total)</option>
            </select>
          </label>
          <div className="form-actions">
            <button data-testid="export-run" className="primary-btn" disabled={busy} onClick={run}>{busy ? "Mengekspor..." : "Ekspor Sekarang"}</button>
            <button data-testid="export-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WarehouseModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", location: "" });
  const [error, setError] = useState("");
  const submit = async () => {
    try { onDone(await api("/warehouses", { method: "POST", body: JSON.stringify(form) })); }
    catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-overlay" data-testid="warehouse-modal" onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">GUDANG</p><h2>Tambah Gudang Baru</h2></div><button className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          {error && <div className="journal-balance bad">{error}</div>}
          <div className="form-grid">
            <label>Nama Gudang<input data-testid="warehouse-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>Lokasi<input data-testid="warehouse-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></label>
          </div>
          <div className="form-actions">
            <button data-testid="warehouse-save" className="primary-btn" disabled={!form.name} onClick={submit}>Simpan Gudang</button>
            <button data-testid="warehouse-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OpnameModal({ warehouses, products, onClose, onDone }) {
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || "");
  const [whStock, setWhStock] = useState({});
  const [date, setDate] = useState(today());
  const [memo, setMemo] = useState("");
  const [lines, setLines] = useState([{ product_id: "", counted_qty: "" }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (warehouseId) api(`/inventory/warehouse-stock/${warehouseId}`).then((r) => setWhStock(r.stock)).catch(() => setWhStock({}));
  }, [warehouseId]);
  const setLine = (i, k, v) => setLines(lines.map((l, x) => (x === i ? { ...l, [k]: v } : l)));
  const productById = (id) => products.find((p) => p.id === id);
  const valid = warehouseId && date && lines.some((l) => l.product_id && l.counted_qty !== "");
  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = { warehouse_id: warehouseId, date, memo, lines: lines.filter((l) => l.product_id && l.counted_qty !== "").map((l) => ({ product_id: l.product_id, counted_qty: Number(l.counted_qty) })) };
      onDone(await api("/stock-adjustments", { method: "POST", body: JSON.stringify(payload) }));
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="opname-modal" onClick={onClose}>
      <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">STOCK OPNAME</p><h2>Sesuaikan Stok</h2></div><button data-testid="opname-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          {error && <div className="journal-balance bad" data-testid="opname-error">{error}</div>}
          <div className="form-grid payment-head-grid">
            <label>Gudang
              <select data-testid="opname-warehouse-select" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">Pilih gudang...</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name} {w.location ? `· ${w.location}` : ""}</option>)}
              </select>
            </label>
            <label>Tanggal<input data-testid="opname-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
            <label>Memo<input data-testid="opname-memo-input" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Opsional" /></label>
          </div>
          <table className="journal-line-table" data-testid="opname-line-table">
            <thead><tr><th>PRODUK</th><th className="right">STOK TERCATAT</th><th className="right">JUMLAH FISIK</th><th className="right">SELISIH</th><th /></tr></thead>
            <tbody>
              {lines.map((l, i) => {
                const before = l.product_id ? (whStock[l.product_id] ?? 0) : "";
                const diff = l.product_id && l.counted_qty !== "" ? Number(l.counted_qty) - Number(before || 0) : "";
                return (
                  <tr key={i}>
                    <td>
                      <select data-testid={`opname-line-product-${i}`} value={l.product_id} onChange={(e) => setLine(i, "product_id", e.target.value)}>
                        <option value="">Pilih produk...</option>
                        {products.map((p) => <option key={p.id} value={p.id}>{`${p.name} [${p.sku}]`}</option>)}
                      </select>
                    </td>
                    <td className="right mono" data-testid={`opname-line-before-${i}`}>{before === "" ? "—" : before}</td>
                    <td><input data-testid={`opname-line-counted-${i}`} className="right" type="number" min="0" value={l.counted_qty} onChange={(e) => setLine(i, "counted_qty", e.target.value)} /></td>
                    <td className={`right mono ${diff < 0 ? "negative-amount" : ""}`} data-testid={`opname-line-diff-${i}`}>{diff === "" ? "—" : diff > 0 ? `+${diff}` : diff}</td>
                    <td>{lines.length > 1 && <button className="icon-btn" onClick={() => setLines(lines.filter((_, x) => x !== i))}><Trash2 size={14} /></button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button data-testid="opname-add-line" className="text-btn" onClick={() => setLines([...lines, { product_id: "", counted_qty: "" }])}><Plus size={14} /> Tambah produk</button>
          <div className="form-actions">
            <button data-testid="opname-save" className="primary-btn" disabled={!valid || saving} onClick={submit}>{saving ? "Menyimpan..." : "Simpan Penyesuaian"}</button>
            <button data-testid="opname-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WhTransferModal({ warehouses, products, onClose, onDone }) {
  const [form, setForm] = useState({ from_warehouse_id: warehouses[0]?.id || "", to_warehouse_id: "", date: today(), memo: "" });
  const [srcStock, setSrcStock] = useState({});
  const [lines, setLines] = useState([{ product_id: "", qty: "" }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (form.from_warehouse_id) api(`/inventory/warehouse-stock/${form.from_warehouse_id}`).then((r) => setSrcStock(r.stock)).catch(() => setSrcStock({}));
  }, [form.from_warehouse_id]);
  const setLine = (i, k, v) => setLines(lines.map((l, x) => (x === i ? { ...l, [k]: v } : l)));
  const valid = form.from_warehouse_id && form.to_warehouse_id && form.from_warehouse_id !== form.to_warehouse_id && form.date && lines.some((l) => l.product_id && Number(l.qty) > 0);
  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...form, lines: lines.filter((l) => l.product_id && Number(l.qty) > 0).map((l) => ({ product_id: l.product_id, qty: Number(l.qty) })) };
      onDone(await api("/warehouse-transfers", { method: "POST", body: JSON.stringify(payload) }));
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="wh-transfer-modal" onClick={onClose}>
      <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">TRANSFER GUDANG</p><h2>Transfer Antar Gudang</h2></div><button data-testid="wh-transfer-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          {error && <div className="journal-balance bad" data-testid="wh-transfer-error">{error}</div>}
          <div className="form-grid payment-head-grid">
            <label>Dari gudang
              <select data-testid="wh-transfer-from" value={form.from_warehouse_id} onChange={(e) => setForm({ ...form, from_warehouse_id: e.target.value })}>
                <option value="">Pilih gudang asal...</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
            <label>Ke gudang
              <select data-testid="wh-transfer-to" value={form.to_warehouse_id} onChange={(e) => setForm({ ...form, to_warehouse_id: e.target.value })}>
                <option value="">Pilih gudang tujuan...</option>
                {warehouses.filter((w) => w.id !== form.from_warehouse_id).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
            <label>Tanggal<input data-testid="wh-transfer-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
            <label>Memo<input data-testid="wh-transfer-memo" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="Opsional" /></label>
          </div>
          <table className="journal-line-table" data-testid="wh-transfer-line-table">
            <thead><tr><th>PRODUK</th><th className="right">STOK DI GUDANG ASAL</th><th className="right">JUMLAH TRANSFER</th><th /></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <select data-testid={`wh-transfer-product-${i}`} value={l.product_id} onChange={(e) => setLine(i, "product_id", e.target.value)}>
                      <option value="">Pilih produk...</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{`${p.name} [${p.sku}]`}</option>)}
                    </select>
                  </td>
                  <td className="right mono">{l.product_id ? (srcStock[l.product_id] ?? 0) : "—"}</td>
                  <td><input data-testid={`wh-transfer-qty-${i}`} className="right" type="number" min="0" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} /></td>
                  <td>{lines.length > 1 && <button className="icon-btn" onClick={() => setLines(lines.filter((_, x) => x !== i))}><Trash2 size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button data-testid="wh-transfer-add-line" className="text-btn" onClick={() => setLines([...lines, { product_id: "", qty: "" }])}><Plus size={14} /> Tambah produk</button>
          <div className="form-actions">
            <button data-testid="wh-transfer-save" className="primary-btn" disabled={!valid || saving} onClick={submit}>{saving ? "Menyimpan..." : "Transfer Sekarang"}</button>
            <button data-testid="wh-transfer-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}
